import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MemberSwaps from './MemberSwaps';
import {
  Box, Typography, Paper, Chip, IconButton, Grid, CircularProgress,
  Alert, Divider, useMediaQuery, useTheme, Tooltip
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Shield as ShieldIcon,
  AdminPanelSettings as AdminIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { useMemberAuth } from '../context/MemberAuthContext';
import Breadcrumbs from './Breadcrumbs';
import UserHeader from './UserHeader';

const ROLE_LABELS = {
  '1st_safety_officer': '1st Safety Officer',
  '2nd_safety_officer': '2nd Safety Officer',
  'administrator': 'Administrator'
};

const ROLE_COLORS = {
  '1st_safety_officer': '#d32f2f',
  '2nd_safety_officer': '#f57c00',
  'administrator': '#0d2c54'
};

const ROLE_ICONS = {
  '1st_safety_officer': ShieldIcon,
  '2nd_safety_officer': ShieldIcon,
  'administrator': AdminIcon
};

const formatSessionTime = (t) => {
  if (!t) return '';
  return t.split('-').map(part => {
    const segments = part.trim().split(':');
    return segments.length >= 2 ? `${segments[0]}:${segments[1]}` : part;
  }).join(' – ');
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function MemberRota() {
  const { member, isAuthenticated } = useMemberAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(today.toISOString().split('T')[0]);
  const [totalDutyCounts, setTotalDutyCounts] = useState({});
  const [upcomingWithTeam, setUpcomingWithTeam] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/members');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    fetchRotaMonth();
  }, [currentMonth, currentYear]);

  useEffect(() => {
    fetchDutyCounts();
    if (member?.id) fetchUpcomingDuties();
  }, [member?.id]);

  const fetchUpcomingDuties = async () => {
    setUpcomingLoading(true);
    try {
      const res = await fetch(`/api/rota/upcoming?memberId=${member.id}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('memberToken') || sessionStorage.getItem('adminToken') || ''}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setUpcomingWithTeam(data.upcoming || []);
    } catch (err) {
      console.error('Failed to fetch upcoming duties', err);
    } finally {
      setUpcomingLoading(false);
    }
  };

  const fetchDutyCounts = async () => {
    try {
      const res = await fetch('/api/rota/duty-counts');
      if (!res.ok) return;
      const data = await res.json();
      setTotalDutyCounts(data.counts || {});
    } catch (err) {
      console.error('Failed to fetch duty counts', err);
    }
  };

  const initialLoadDone = React.useRef(false);

  const fetchRotaMonth = async () => {
    if (!initialLoadDone.current) setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/rota/month?year=${currentYear}&month=${currentMonth + 1}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('memberToken') || sessionStorage.getItem('adminToken') || ''}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setAssignments(data.assignments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };

  const assignmentsByDate = useMemo(() => {
    const map = {};
    assignments.forEach(a => {
      const dateKey = a.session_date.split('T')[0];
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(a);
    });
    return map;
  }, [assignments]);

  const myAssignments = useMemo(() => {
    if (!member?.id) return [];
    return assignments
      .filter(a => a.member_id === member.id)
      .sort((a, b) => a.session_date.localeCompare(b.session_date));
  }, [assignments, member]);

  const myDutyDates = useMemo(() => {
    return new Set(myAssignments.map(a => a.session_date.split('T')[0]));
  }, [myAssignments]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(d);
    }
    return days;
  }, [currentMonth, currentYear]);

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day) => {
    if (!day) return;
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
  };

  const selectedDateAssignments = useMemo(() => {
    return assignmentsByDate[selectedDate] || [];
  }, [assignmentsByDate, selectedDate]);

  const groupedBySession = useMemo(() => {
    const groups = {};
    selectedDateAssignments.forEach(a => {
      const key = a.session_time;
      if (!groups[key]) groups[key] = { time: a.session_time, type: a.session_type, roles: [] };
      groups[key].roles.push(a);
    });
    return Object.values(groups);
  }, [selectedDateAssignments]);

  const selectedDateObj = selectedDate ? new Date(selectedDate + 'T12:00:00') : null;
  const selectedDayName = selectedDateObj ? selectedDateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const upcomingDuties = useMemo(() => {
    const todayStr = today.toISOString().split('T')[0];
    return myAssignments.filter(a => a.session_date.split('T')[0] >= todayStr).slice(0, 8);
  }, [myAssignments]);

  const renderCalendar = () => (
    <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={prevMonth} size="small"><ChevronLeftIcon /></IconButton>
        <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54' }}>
          {MONTH_NAMES[currentMonth]} {currentYear}
        </Typography>
        <IconButton onClick={nextMonth} size="small"><ChevronRightIcon /></IconButton>
      </Box>

      <Grid container spacing={0}>
        {DAY_NAMES.map(d => (
          <Grid item xs={12/7} key={d}>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', fontWeight: 600, color: '#666', pb: 0.5 }}>
              {d}
            </Typography>
          </Grid>
        ))}
        {calendarDays.map((day, i) => {
          if (!day) return <Grid item xs={12/7} key={`empty-${i}`}><Box sx={{ height: 44 }} /></Grid>;

          const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const hasAssignments = !!assignmentsByDate[dateStr];
          const isMyDuty = myDutyDates.has(dateStr);
          const isSelected = dateStr === selectedDate;
          const isToday = dateStr === today.toISOString().split('T')[0];

          return (
            <Grid item xs={12/7} key={day}>
              <Box
                onClick={() => handleDateClick(day)}
                sx={{
                  height: 44,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: hasAssignments ? 'pointer' : 'default',
                  borderRadius: 2,
                  position: 'relative',
                  backgroundColor: isSelected ? '#0d2c54' : isMyDuty ? '#dbf14e' : 'transparent',
                  color: isSelected ? '#fff' : '#333',
                  border: isToday ? '2px solid #0d2c54' : 'none',
                  transition: 'all 0.15s',
                  '&:hover': hasAssignments ? {
                    backgroundColor: isSelected ? '#0d2c54' : '#e8eaf6',
                  } : {},
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: isMyDuty || isToday ? 700 : 400, fontSize: '0.85rem' }}>
                  {day}
                </Typography>
                {hasAssignments && (
                  <Box sx={{ display: 'flex', gap: '2px', mt: '1px' }}>
                    {isMyDuty && <CircleIcon sx={{ fontSize: 5, color: isSelected ? '#dbf14e' : '#d32f2f' }} />}
                    {!isMyDuty && <CircleIcon sx={{ fontSize: 5, color: isSelected ? 'rgba(255,255,255,0.5)' : '#bbb' }} />}
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#dbf14e', borderRadius: 1 }} />
          <Typography variant="caption" sx={{ color: '#666' }}>Your duty</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, border: '2px solid #0d2c54', borderRadius: 1 }} />
          <Typography variant="caption" sx={{ color: '#666' }}>Today</Typography>
        </Box>
      </Box>
    </Paper>
  );

  const renderAssignmentCard = (assignment) => {
    const isMe = assignment.member_id === member?.id;
    const RoleIcon = ROLE_ICONS[assignment.role_type] || PersonIcon;
    const showPhone = assignment.incl_phone_in_rota === 'Yes' && assignment.phone_number;

    return (
      <Box
        key={assignment.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 1.5,
          borderRadius: 2,
          backgroundColor: isMe ? '#f5fbe6' : '#fafafa',
          border: isMe ? '1px solid #dbf14e' : '1px solid #eee',
          mb: 0.5,
        }}
      >
        <RoleIcon sx={{ fontSize: 18, color: ROLE_COLORS[assignment.role_type] || '#666' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: isMe ? 700 : 400, fontSize: '0.85rem' }}>
              {assignment.member_name || 'Unassigned'}
            </Typography>
            {isMe && <Chip label="You" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#dbf14e' }} />}
          </Box>
          <Typography variant="caption" sx={{ color: ROLE_COLORS[assignment.role_type] || '#666' }}>
            {ROLE_LABELS[assignment.role_type] || assignment.role_type}
          </Typography>
        </Box>
        {!isMe && (assignment.email || assignment.phone) && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {assignment.email && (
              <Tooltip title={assignment.email}>
                <IconButton href={`mailto:${assignment.email}`} sx={{ p: 1, color: '#999', minWidth: 40, minHeight: 40 }}>
                  <EmailIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            {assignment.phone && (
              <Tooltip title={assignment.phone}>
                <IconButton href={`tel:${assignment.phone}`} sx={{ p: 1, color: '#999', minWidth: 40, minHeight: 40 }}>
                  <PhoneIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderDutyList = () => (
    <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
      <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54', mb: 1.5 }}>
        {selectedDayName || 'Select a date'}
      </Typography>

      {selectedDate && groupedBySession.length === 0 && (
        <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
          No sessions scheduled for this date.
        </Typography>
      )}

      {groupedBySession.map((session, idx) => (
        <Box key={idx} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={formatSessionTime(session.time)}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                backgroundColor: session.type === 'school_holiday' ? '#fff3e0' : '#e3f2fd',
                color: session.type === 'school_holiday' ? '#e65100' : '#0d2c54',
              }}
            />
            {session.type === 'school_holiday' && (
              <Typography variant="caption" sx={{ color: '#e65100' }}>School Holiday</Typography>
            )}
          </Box>
          {session.roles.map(a => renderAssignmentCard(a))}
        </Box>
      ))}

    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <UserHeader />
      <Box sx={{
        background: 'linear-gradient(135deg, #0d2c54 0%, #1a4778 100%)',
        py: { xs: 3, md: 4 },
        px: 2,
        textAlign: 'center',
        color: '#fff',
      }}>
        <Typography variant="h4" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 700 }}>
          Duty Rota
        </Typography>
        <Typography variant="body1" sx={{ mt: 0.5, opacity: 0.8 }}>
          View your upcoming duties and see who's on duty each day
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 1.5, md: 3 }, py: 3 }}>
        <Breadcrumbs parents={[{ path: '/members', label: 'Members Area' }]} />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!loading && !upcomingLoading && upcomingWithTeam.length === 0 && assignments.length === 0 && (
          <Paper elevation={1} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54', mb: 1 }}>
              No Rota Available Yet
            </Typography>
            <Typography variant="body1" sx={{ color: '#666' }}>
              The duty rota will be shown here once it has been published. Please check back later.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: '#888', fontSize: '0.8rem' }}>
              If you have any questions, please contact{' '}
              <a href="mailto:contact@comberton-pool.org.uk" style={{ color: '#0d2c54', fontWeight: 600 }}>
                contact@comberton-pool.org.uk
              </a>
            </Typography>
          </Paper>
        )}

        {!loading && (assignments.length > 0 || upcomingWithTeam.length > 0) && (
          <>
            {upcomingLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : upcomingWithTeam.length > 0 && (
              <Paper elevation={1} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54', mb: 1.5, px: 0.5 }}>
                  Your Upcoming Duties
                </Typography>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px', color: '#666', fontWeight: 600, fontSize: '0.75rem' }}>Date / Time</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px', color: '#666', fontWeight: 600, fontSize: '0.75rem' }}>Team</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingWithTeam.map((duty, idx) => {
                      const dateObj = new Date(duty.date + 'T12:00:00');
                      const dateLabel = dateObj.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                      const timeLabel = formatSessionTime(duty.session_time);
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbff' }}>
                          <td style={{ padding: '6px 8px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                            <div style={{ fontWeight: 600, color: '#0d2c54' }}>{dateLabel}</div>
                            <div style={{ color: '#666', fontSize: '0.75rem' }}>{timeLabel}</div>
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {duty.team.map((tm, tIdx) => {
                              const roleColor = ROLE_COLORS[tm.role_type] || '#666';
                              const roleShort = tm.role_type === '1st_safety_officer' ? '1st' : tm.role_type === '2nd_safety_officer' ? '2nd' : 'Admin';
                              return (
                                <span key={tIdx} style={{ display: 'inline-flex', alignItems: 'center', marginRight: 12, whiteSpace: 'nowrap' }}>
                                  <span style={{ color: roleColor, fontWeight: 600, fontSize: '0.7rem', marginRight: 3 }}>{roleShort}</span>
                                  <span style={{ fontWeight: tm.is_me ? 700 : 400, color: tm.is_me ? '#2e7d32' : '#333' }}>
                                    {tm.member_name}{tm.is_me ? ' (You)' : ''}
                                  </span>
                                  {!tm.is_me && tm.email && (
                                    <Tooltip title={tm.email}><IconButton href={`mailto:${tm.email}`} sx={{ p: 0.5, ml: 0.25, color: '#999', minWidth: 32, minHeight: 32 }}><EmailIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                  )}
                                  {!tm.is_me && tm.phone && (
                                    <Tooltip title={tm.phone}><IconButton href={`tel:${tm.phone}`} sx={{ p: 0.5, ml: 0.25, color: '#999', minWidth: 32, minHeight: 32 }}><PhoneIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                  )}
                                </span>
                              );
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Typography variant="body2" sx={{ mt: 1.5, color: '#888', fontSize: '0.75rem' }}>
                  If you believe there is an error in the rota or you have arranged a swap that isn't showing, please contact{' '}
                  <a href="mailto:contact@comberton-pool.org.uk" style={{ color: '#0d2c54', fontWeight: 600 }}>
                    contact@comberton-pool.org.uk
                  </a>
                </Typography>
              </Paper>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                {renderCalendar()}
              </Grid>
              <Grid item xs={12} md={7}>
                {renderDutyList()}
              </Grid>
            </Grid>
            <MemberSwaps member={member} />
          </>
        )}
      </Box>
    </Box>
  );
}

export default MemberRota;
